import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Grid,
  Link,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import {
  ArrowBack,
  AssignmentTurnedIn,
  CheckCircle,
  FactCheck,
  GpsFixed,
  LocationOn,
  RadioButtonUnchecked,
  Send,
  WarningAmber
} from '@mui/icons-material';
import contractorVerificationService from '../services/contractorVerificationService';
import MapPinPicker from '../components/common/MapPinPicker';
import verifierAgreementService from '../services/verifierAgreementService';

const emptyRepresentative = {
  representativeName: '',
  representativeRole: '',
  idDocumentChecked: false,
  companyAuthorityChecked: false,
  phoneConfirmed: false,
  emailConfirmed: false,
  independentCallbackNumber: '',
  nameMatchesCompanyRecords: false,
  status: 'verified',
  notes: ''
};

const emptyVisit = {
  portfolioEvidenceId: '',
  projectTitle: '',
  projectLocation: '',
  siteContactName: '',
  siteContactPhone: '',
  visitStatus: 'verified',
  gpsLocation: null,
  geocodedLocation: null,
  workConfirmedByContact: false,
  photoUrlsText: '',
  notes: ''
};

const emptyRecommendation = {
  recommendation: 'approve',
  riskLevel: 'standard',
  confidenceScore: 75,
  verifierSummary: ''
};

const DOC_TYPE_LABELS = {
  company_registration: 'Company registration',
  director_id: 'Owner/director ID',
  tax_clearance: 'ZIMRA tax clearance'
};
const docTypeLabel = (type) => DOC_TYPE_LABELS[type] || (type ? type.replaceAll('_', ' ') : 'Document');
const formatDate = (value) => {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString();
};

const Field = ({ label, value }) => {
  if (value === null || value === undefined || value === '') return null;
  return (
    <Box sx={{ minWidth: 180 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{label}</Typography>
      <Typography variant="body2">{value}</Typography>
    </Box>
  );
};

// Read-only view of everything the contractor submitted, so the verifier can
// confirm authority, call references and review portfolio evidence. All data
// comes from the already-loaded `application` object — no extra API calls.
const ContractorSubmissionPanel = ({ application }) => {
  if (!application) return null;
  const documents = application.documents || [];
  const portfolio = application.portfolioEvidence || [];
  const references = application.references || [];
  const specializations = (application.specializations || []).join(', ');
  const serviceAreas = (application.service_areas || []).join(', ');

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" gutterBottom>Contractor Submission</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Everything the contractor submitted. Use it to confirm authority, call references and review portfolio evidence before recording your findings below.
      </Typography>

      <Typography variant="subtitle2" gutterBottom>Profile &amp; Contact</Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <Field label="Legal name" value={application.legal_name} />
        <Field label="Trading name" value={application.trading_name} />
        <Field label="Phone" value={application.phone_number} />
        <Field label="Email" value={application.contractor_email} />
        <Field label="Operating address" value={application.operating_address} />
        <Field label="Company reg. number" value={application.company_registration_number} />
        <Field label="Tax identifier" value={application.tax_identifier} />
        <Field label="Tax clearance number" value={application.tax_clearance_number} />
        <Field label="Tax clearance expires" value={formatDate(application.tax_clearance_expires_at)} />
        <Field label="Primary trade" value={application.primary_trade} />
        <Field label="Specializations" value={specializations} />
        <Field label="Service areas" value={serviceAreas} />
      </Box>
      <Divider sx={{ my: 2 }} />

      <Typography variant="subtitle2" gutterBottom>Documents</Typography>
      {documents.length ? (
        <Stack spacing={1} sx={{ mb: 2 }}>
          {documents.map((doc) => (
            <Stack key={doc.id} direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }}>
              <Typography variant="body2" sx={{ flex: 1 }}>
                <strong>{docTypeLabel(doc.document_type)}</strong>
                {doc.document_name ? ` — ${doc.document_name}` : ''}
                {doc.reference_number ? ` · Ref ${doc.reference_number}` : ''}
                {doc.issuing_authority ? ` · ${doc.issuing_authority}` : ''}
                {formatDate(doc.expires_at) ? ` · expires ${formatDate(doc.expires_at)}` : ''}
              </Typography>
              <Chip
                size="small"
                label={(doc.status || 'pending').replaceAll('_', ' ')}
                color={doc.status === 'verified' ? 'success' : doc.status === 'rejected' ? 'error' : 'default'}
              />
              {doc.file_url && (
                <Link href={doc.file_url} target="_blank" rel="noopener noreferrer" variant="body2">View</Link>
              )}
            </Stack>
          ))}
        </Stack>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>No documents uploaded.</Typography>
      )}
      <Divider sx={{ my: 2 }} />

      <Typography variant="subtitle2" gutterBottom>Portfolio</Typography>
      {portfolio.length ? (
        <Stack spacing={2} sx={{ mb: 2 }}>
          {portfolio.map((proj) => (
            <Box key={proj.id}>
              <Typography variant="body2">
                <strong>{proj.project_title}</strong>
                {proj.project_location ? ` · ${proj.project_location}` : ''}
                {proj.project_value ? ` · ${proj.currency || 'USD'} ${Number(proj.project_value).toLocaleString()}` : ''}
                {formatDate(proj.completed_at) ? ` · completed ${formatDate(proj.completed_at)}` : ''}
              </Typography>
              {(proj.client_name || proj.client_phone || proj.client_email) && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  Client: {[proj.client_name, proj.client_phone, proj.client_email].filter(Boolean).join(' · ')}
                </Typography>
              )}
              {(proj.evidence_urls || []).length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                  {proj.evidence_urls.map((url, i) => (
                    <a key={i} href={url} target="_blank" rel="noopener noreferrer">
                      <Box
                        component="img"
                        src={url}
                        alt={`${proj.project_title} photo ${i + 1}`}
                        sx={{ width: 96, height: 96, objectFit: 'cover', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}
                      />
                    </a>
                  ))}
                </Box>
              )}
            </Box>
          ))}
        </Stack>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>No portfolio projects submitted.</Typography>
      )}
      <Divider sx={{ my: 2 }} />

      <Typography variant="subtitle2" gutterBottom>References</Typography>
      {references.length ? (
        <Stack spacing={1.5}>
          {references.map((ref) => {
            const meta = [
              ref.rating ? `Rating ${ref.rating}/5` : null,
              ref.would_rehire === null || ref.would_rehire === undefined ? null : `Would rehire: ${ref.would_rehire ? 'Yes' : 'No'}`,
              ref.feedback ? `“${ref.feedback}”` : null
            ].filter(Boolean).join(' · ');
            return (
              <Box key={ref.id}>
                <Typography variant="body2">
                  <strong>{ref.referee_name}</strong>
                  {ref.relationship ? ` · ${ref.relationship}` : ''}
                  {ref.project_title ? ` · ${ref.project_title}` : ''}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  {[ref.referee_phone, ref.referee_email].filter(Boolean).join(' · ') || 'No contact details provided'}
                </Typography>
                {meta && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{meta}</Typography>
                )}
              </Box>
            );
          })}
        </Stack>
      ) : (
        <Typography variant="body2" color="text.secondary">No references submitted.</Typography>
      )}
    </Paper>
  );
};

const VerifierContractorVerificationPage = () => {
  const { assignmentId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [agreementAccepted, setAgreementAccepted] = useState(null); // null=loading, true/false=known
  const [assignment, setAssignment] = useState(null);
  const [application, setApplication] = useState(null);
  const [representative, setRepresentative] = useState(emptyRepresentative);
  const [visit, setVisit] = useState(emptyVisit);
  const [recommendation, setRecommendation] = useState(emptyRecommendation);
  const [siteVisits, setSiteVisits] = useState([]);
  const [representativeVerified, setRepresentativeVerified] = useState(false);
  const [references, setReferences] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [pinOpen, setPinOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await contractorVerificationService.getVerifierAssignment(assignmentId);
      const data = response.data;
      setAssignment(data.assignment);
      setApplication(data.application);
      setSiteVisits(data.siteVisits || []);
      setReferences(data.references || []);
      // Mirror the backend submit gate (submitVerifierEvidencePack): the authority
      // check must actually be saved AND verified — the local `representative` form
      // defaults status to 'verified', so it can't tell us this on its own.
      setRepresentativeVerified(data.representativeCheck?.status === 'verified');
      if (data.representativeCheck) {
        setRepresentative({
          representativeName: data.representativeCheck.representative_name || '',
          representativeRole: data.representativeCheck.representative_role || '',
          idDocumentChecked: Boolean(data.representativeCheck.id_document_checked),
          companyAuthorityChecked: Boolean(data.representativeCheck.company_authority_checked),
          phoneConfirmed: Boolean(data.representativeCheck.phone_confirmed),
          emailConfirmed: Boolean(data.representativeCheck.email_confirmed),
          independentCallbackNumber: data.representativeCheck.independent_callback_number || '',
          nameMatchesCompanyRecords: Boolean(data.representativeCheck.name_matches_company_records),
          status: data.representativeCheck.status || 'verified',
          notes: data.representativeCheck.notes || ''
        });
      }
      setRecommendation({
        recommendation: data.assignment.recommendation || 'approve',
        riskLevel: data.assignment.risk_level || 'standard',
        confidenceScore: data.assignment.confidence_score || 75,
        verifierSummary: data.assignment.verifier_summary || ''
      });
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load contractor file');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    verifierAgreementService.getAgreement()
      .then((data) => setAgreementAccepted(data.accepted))
      .catch(() => setAgreementAccepted(true)); // fail-open: don't hard-block on service error
  }, [assignmentId]);

  const gpsRequired = assignment?.gps_proof_mode === 'required';
  // 0 = site visits not required for this file (the default since 18 Sep 2026);
  // an admin can still mandate a count per assignment.
  const requiredVisits = Math.max(0, parseInt(assignment?.required_site_visit_count, 10) || 0);
  const verifiedVisitCount = siteVisits.filter((item) => item.visit_status === 'verified').length;

  // Mirror the server-side submit gates (submitVerifierEvidencePack) so the verifier
  // sees exactly WHAT is still outstanding instead of clicking Submit and getting a
  // raw 400 with no guidance — the "it's not working" Chari hit.
  const countedVisits = siteVisits
    .filter((item) => item.visit_status === 'verified')
    .slice(0, requiredVisits);
  const gpsProofMet = !gpsRequired || countedVisits.every((item) => item.gps_status === 'verified');
  const submitPrereqs = [
    { key: 'rep', label: 'Representative authority check verified', met: representativeVerified },
    // Site visits only gate Submit when the admin mandated a count for this file.
    ...(requiredVisits > 0
      ? [{
        key: 'visits',
        label: `${requiredVisits} verified site visits recorded`,
        met: verifiedVisitCount >= requiredVisits,
        detail: `${verifiedVisitCount}/${requiredVisits} recorded`
      }]
      : []),
    ...(gpsRequired && requiredVisits > 0
      ? [{
          key: 'gps',
          label: 'On-site GPS check within range for each counted visit',
          met: verifiedVisitCount >= requiredVisits && gpsProofMet
        }]
      : [])
  ];
  const outstandingPrereqs = submitPrereqs.filter((item) => !item.met);
  const prereqsMet = outstandingPrereqs.length === 0;

  const captureGps = () => {
    setError('');
    if (!navigator.geolocation) {
      setError('This browser does not support GPS capture.');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setVisit((prev) => ({
          ...prev,
          gpsLocation: {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
            timestamp: new Date().toISOString()
          }
        }));
      },
      () => setError('Unable to capture GPS. Check browser location permission or ask admin to override GPS proof for this assignment.'),
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  };

  const saveRepresentative = async () => {
    setSaving(true);
    setError('');
    try {
      await contractorVerificationService.saveRepresentativeCheck(assignmentId, representative);
      setSuccess('Representative check saved');
      await load();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save representative check');
    } finally {
      setSaving(false);
    }
  };

  const addVisit = async () => {
    setSaving(true);
    setError('');
    try {
      await contractorVerificationService.addSiteVisit(assignmentId, {
        ...visit,
        portfolioEvidenceId: visit.portfolioEvidenceId || null,
        photoUrls: visit.photoUrlsText.split('\n').map((item) => item.trim()).filter(Boolean)
      });
      setVisit(emptyVisit);
      setSuccess('Site visit saved');
      await load();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save site visit');
    } finally {
      setSaving(false);
    }
  };

  const updateReference = async (referenceId, patch) => {
    setSaving(true);
    setError('');
    try {
      await contractorVerificationService.updateReferenceCheck(assignmentId, referenceId, patch);
      setSuccess('Reference status saved');
      await load();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update reference');
    } finally {
      setSaving(false);
    }
  };

  const submitPack = async () => {
    setSaving(true);
    setError('');
    try {
      await contractorVerificationService.submitVerifierEvidencePack(assignmentId, {
        ...recommendation,
        confidenceScore: Number(recommendation.confidenceScore)
      });
      setSuccess('Evidence pack submitted for admin review');
      await load();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to submit evidence pack');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  // Hard gate: agreement must be accepted before the verifier can do contractor work
  if (agreementAccepted === false) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/verifier/my-work')} sx={{ mb: 2 }}>
          Back to My Work
        </Button>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Alert severity="warning" icon={<WarningAmber />} sx={{ mb: 3 }}>
            <strong>Agreement required.</strong> You must sign the Field Verifier Agreement before you can work on contractor verification files.
          </Alert>
          <Button variant="contained" size="large" onClick={() => navigate('/verifier/agreement')}>
            Sign the Field Verifier Agreement
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Button startIcon={<ArrowBack />} onClick={() => navigate('/verifier/my-work')} sx={{ mb: 2 }}>
        Back to My Work
      </Button>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', md: 'center' }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h4">Contractor Verification File</Typography>
            <Typography color="text.secondary">
              {application?.trading_name || application?.company_name || application?.legal_name || application?.contractor_email}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {[application?.phone_number, application?.contractor_email].filter(Boolean).join(' · ') || 'No contact details on file'}
            </Typography>
          </Box>
          <Chip label={assignment?.status?.replaceAll('_', ' ') || 'assigned'} />
          <Chip
            label={requiredVisits > 0 ? `${verifiedVisitCount}/${requiredVisits} site visits` : `${verifiedVisitCount} site visits (optional)`}
            color={verifiedVisitCount >= requiredVisits ? 'success' : 'warning'}
          />
          <Chip label={gpsRequired ? 'GPS required' : 'GPS admin override'} color={gpsRequired ? 'default' : 'warning'} />
        </Stack>
        {!gpsRequired && (
          <Alert severity="warning" icon={<WarningAmber />} sx={{ mt: 2 }}>
            Admin disabled GPS proof for this assignment. Add photos, site contact confirmation, directions/address notes, and a clear reason in your visit notes.
          </Alert>
        )}
      </Paper>

      <ContractorSubmissionPanel application={application} />

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
              <FactCheck color="primary" />
              <Typography variant="h6">Representative Authority</Typography>
            </Stack>
            <Stack spacing={2}>
              <TextField label="Representative name" value={representative.representativeName} onChange={(e) => setRepresentative((prev) => ({ ...prev, representativeName: e.target.value }))} />
              <TextField label="Representative role" value={representative.representativeRole} onChange={(e) => setRepresentative((prev) => ({ ...prev, representativeRole: e.target.value }))} />
              <TextField
                label="Independent callback number"
                helperText="A number you sourced independently (e.g. company listing) and called to confirm authority — not only the one on the application."
                value={representative.independentCallbackNumber}
                onChange={(e) => setRepresentative((prev) => ({ ...prev, independentCallbackNumber: e.target.value }))}
              />
              <TextField select label="Status" value={representative.status} onChange={(e) => setRepresentative((prev) => ({ ...prev, status: e.target.value }))}>
                <MenuItem value="verified">Verified</MenuItem>
                <MenuItem value="needs_more_info">Needs more info</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
              </TextField>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {[
                  ['idDocumentChecked', 'ID checked'],
                  ['companyAuthorityChecked', 'Company authority'],
                  ['nameMatchesCompanyRecords', 'Name matches records'],
                  ['phoneConfirmed', 'Phone confirmed'],
                  ['emailConfirmed', 'Email confirmed']
                ].map(([key, label]) => (
                  <Button
                    key={key}
                    size="small"
                    variant={representative[key] ? 'contained' : 'outlined'}
                    onClick={() => setRepresentative((prev) => ({ ...prev, [key]: !prev[key] }))}
                  >
                    {label}
                  </Button>
                ))}
              </Stack>
              <TextField multiline minRows={3} label="Notes" value={representative.notes} onChange={(e) => setRepresentative((prev) => ({ ...prev, notes: e.target.value }))} />
              <Button variant="contained" onClick={saveRepresentative} disabled={saving}>Save Representative Check</Button>
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <LocationOn color="primary" />
              <Typography variant="h6">Project Site Visit</Typography>
              <Chip
                size="small"
                label={requiredVisits > 0 ? `${verifiedVisitCount}/${requiredVisits} recorded` : `${verifiedVisitCount} recorded · optional`}
                color={verifiedVisitCount >= requiredVisits ? 'success' : 'warning'}
              />
            </Stack>
            <Alert severity="info" sx={{ mb: 2 }}>
              Reviewing a portfolio project above is desk review — it does <strong>not</strong> count as a site visit.
              {requiredVisits > 0 ? (
                <> To submit, record <strong>{requiredVisits} on-site visits</strong> here{gpsRequired ? ', each with a live on-site GPS check within range of the project' : ' with compensating evidence (photos + site contact)'}.</>
              ) : (
                <> Site visits are <strong>not required</strong> for this file, but any you record here{gpsRequired ? ' with a live GPS check' : ''} strengthen the report the admin sees.</>
              )}
            </Alert>
            <Stack spacing={2}>
              <TextField select label="Submitted portfolio project" value={visit.portfolioEvidenceId} onChange={(e) => {
                const selected = application?.portfolioEvidence?.find((item) => item.id === e.target.value);
                setVisit((prev) => ({
                  ...prev,
                  portfolioEvidenceId: e.target.value,
                  projectTitle: selected?.project_title || prev.projectTitle,
                  projectLocation: selected?.project_location || prev.projectLocation
                }));
              }}>
                <MenuItem value="">Manual / not linked</MenuItem>
                {(application?.portfolioEvidence || []).map((item) => (
                  <MenuItem key={item.id} value={item.id}>{item.project_title}</MenuItem>
                ))}
              </TextField>
              <TextField label="Project title" value={visit.projectTitle} onChange={(e) => setVisit((prev) => ({ ...prev, projectTitle: e.target.value }))} />
              <TextField label="Project location" value={visit.projectLocation} onChange={(e) => setVisit((prev) => ({ ...prev, projectLocation: e.target.value }))} />
              <TextField label="Site contact name" value={visit.siteContactName} onChange={(e) => setVisit((prev) => ({ ...prev, siteContactName: e.target.value }))} />
              <TextField label="Site contact phone" value={visit.siteContactPhone} onChange={(e) => setVisit((prev) => ({ ...prev, siteContactPhone: e.target.value }))} />
              <Button
                startIcon={<LocationOn />}
                variant={visit.geocodedLocation ? 'contained' : 'outlined'}
                color="secondary"
                onClick={() => setPinOpen(true)}
              >
                {visit.geocodedLocation
                  ? `Project pinned (${visit.geocodedLocation.latitude.toFixed(4)}, ${visit.geocodedLocation.longitude.toFixed(4)})`
                  : 'Pin project location on map'}
              </Button>
              <Button startIcon={<GpsFixed />} variant={visit.gpsLocation ? 'contained' : 'outlined'} onClick={captureGps}>
                {visit.gpsLocation ? 'On-site GPS captured' : 'Capture on-site GPS'}
              </Button>
              {gpsRequired && (
                <Typography variant="caption" color="text.secondary">
                  Pin where the project is, then capture your GPS while standing on site. The visit is geo-verified only when your GPS is within range of the pin.
                </Typography>
              )}
              <Button
                variant={visit.workConfirmedByContact ? 'contained' : 'outlined'}
                color="success"
                onClick={() => setVisit((prev) => ({ ...prev, workConfirmedByContact: !prev.workConfirmedByContact }))}
              >
                {visit.workConfirmedByContact ? '✓ On-site contact confirmed the contractor did this work' : 'On-site contact confirmed the contractor did this work?'}
              </Button>
              <TextField multiline minRows={2} label="Photo URLs, one per line" value={visit.photoUrlsText} onChange={(e) => setVisit((prev) => ({ ...prev, photoUrlsText: e.target.value }))} />
              <TextField multiline minRows={3} label="Visit notes" value={visit.notes} onChange={(e) => setVisit((prev) => ({ ...prev, notes: e.target.value }))} />
              <Button variant="contained" onClick={addVisit} disabled={saving || !visit.projectTitle}>Add Site Visit</Button>
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>Evidence Progress</Typography>
            <Stack spacing={1}>
              {siteVisits.map((item) => (
                <Stack key={item.id} direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', md: 'center' }}>
                  <Typography sx={{ flex: 1 }}>{item.project_title}</Typography>
                  <Chip size="small" label={item.visit_status.replaceAll('_', ' ')} />
                  <Chip size="small" label={`GPS ${item.gps_status.replaceAll('_', ' ')}`} color={item.gps_status === 'overridden' ? 'warning' : item.gps_status === 'verified' ? 'success' : 'default'} />
                </Stack>
              ))}
              {!siteVisits.length && <Typography color="text.secondary">No site visits saved yet.</Typography>}
            </Stack>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" gutterBottom>References</Typography>
            <Stack spacing={1}>
              {references.map((item) => (
                <Stack key={item.id} direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', md: 'center' }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography>{item.referee_name} {item.project_title ? `- ${item.project_title}` : ''}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {[item.referee_phone, item.referee_email].filter(Boolean).join(' · ') || 'No contact details provided'}
                    </Typography>
                  </Box>
                  <Chip size="small" label={item.status.replaceAll('_', ' ')} />
                  <Button
                    size="small"
                    variant="outlined"
                    color={item.contact_source === 'independent' ? 'success' : 'inherit'}
                    onClick={() => updateReference(item.id, { contactSource: item.contact_source === 'independent' ? 'contractor_supplied' : 'independent' })}
                    disabled={saving}
                  >
                    {item.contact_source === 'independent' ? 'Independently sourced' : 'Contractor-supplied'}
                  </Button>
                  <Button size="small" onClick={() => updateReference(item.id, { status: 'verified' })} disabled={saving}>Verified</Button>
                  <Button size="small" onClick={() => updateReference(item.id, { status: 'unreachable' })} disabled={saving}>Unreachable</Button>
                  <Button size="small" color="error" onClick={() => updateReference(item.id, { status: 'rejected' })} disabled={saving}>Reject</Button>
                </Stack>
              ))}
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
              <AssignmentTurnedIn color="primary" />
              <Typography variant="h6">Final Recommendation</Typography>
            </Stack>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField select fullWidth label="Recommendation" value={recommendation.recommendation} onChange={(e) => setRecommendation((prev) => ({ ...prev, recommendation: e.target.value }))}>
                  <MenuItem value="approve">Approve</MenuItem>
                  <MenuItem value="approve_with_limits">Approve with limits</MenuItem>
                  <MenuItem value="request_info">Request info</MenuItem>
                  <MenuItem value="reject">Reject</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField select fullWidth label="Risk level" value={recommendation.riskLevel} onChange={(e) => setRecommendation((prev) => ({ ...prev, riskLevel: e.target.value }))}>
                  <MenuItem value="low">Low</MenuItem>
                  <MenuItem value="standard">Standard</MenuItem>
                  <MenuItem value="elevated">Elevated</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField fullWidth type="number" label="Confidence score" value={recommendation.confidenceScore} onChange={(e) => setRecommendation((prev) => ({ ...prev, confidenceScore: e.target.value }))} inputProps={{ min: 0, max: 100 }} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth multiline minRows={4} label="Verifier summary" value={recommendation.verifierSummary} onChange={(e) => setRecommendation((prev) => ({ ...prev, verifierSummary: e.target.value }))} />
              </Grid>
            </Grid>
            <Box sx={{ mt: 3, p: 2, borderRadius: 2, bgcolor: prereqsMet ? 'success.light' : 'grey.100', border: '1px solid', borderColor: prereqsMet ? 'success.main' : 'divider' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                {prereqsMet ? 'Ready to submit' : 'Before you can submit'}
              </Typography>
              <Stack spacing={0.75}>
                {submitPrereqs.map((item) => (
                  <Stack key={item.key} direction="row" spacing={1} alignItems="center">
                    {item.met
                      ? <CheckCircle fontSize="small" color="success" />
                      : <RadioButtonUnchecked fontSize="small" color="disabled" />}
                    <Typography variant="body2" color={item.met ? 'text.primary' : 'text.secondary'}>
                      {item.label}{item.detail ? ` — ${item.detail}` : ''}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
              {!prereqsMet && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  Complete the steps above to enable submission. Your recommendation is saved with the pack.
                </Typography>
              )}
            </Box>
            <Button sx={{ mt: 2 }} variant="contained" startIcon={<Send />} onClick={submitPack} disabled={saving || !prereqsMet || assignment?.status === 'submitted' || agreementAccepted === false}>
              Submit Evidence Pack
            </Button>
          </Paper>
        </Grid>
      </Grid>

      <MapPinPicker
        open={pinOpen}
        onClose={() => setPinOpen(false)}
        initialPin={visit.geocodedLocation ? { lat: visit.geocodedLocation.latitude, lng: visit.geocodedLocation.longitude } : null}
        initialCenter={visit.gpsLocation ? { lat: visit.gpsLocation.latitude, lng: visit.gpsLocation.longitude } : null}
        addressLabel={visit.projectLocation ? `Project: ${visit.projectLocation}` : ''}
        onConfirm={(p) => {
          setVisit((prev) => ({ ...prev, geocodedLocation: { latitude: Number(p.lat), longitude: Number(p.lng) } }));
          setPinOpen(false);
        }}
      />
    </Container>
  );
};

export default VerifierContractorVerificationPage;
